import type { CatalogueProvenanceNote } from "./emitter-catalogue.js";
import type { Ruleset } from "./types.js";

/**
 * Underfloor heating product catalogue: output per m² as a function of FLOOR
 * CONSTRUCTION, floor covering and flow/return water temperature.
 *
 * `EN_1264_UNDERFLOOR_LIMITS` in `emitters.ts` already gives the engine a single,
 * generic BS EN 1264-2 characteristic curve (q = 8.92 × ΔT^1.1) and the BS EN 1264-3
 * surface-temperature ceiling — enough to answer "can a heated floor EVER meet this
 * room's load", which is what `underfloorOutputCeiling` in @sts/calc uses it for.
 *
 * What that single curve cannot answer is the question a heat pump design actually
 * turns on: at THIS flow temperature, with THIS floor construction (pipe centres,
 * deck, floor covering), what does the floor actually deliver? BS EN 1264-2's own
 * construction-specific diagrams answer that, but BS EN 1264-2 is a paid standard
 * and is not among the documents held in the vault (same gap `emitters.ts`'s own
 * PROVENANCE WARNING already states for the generic curve).
 *
 * `JG_SPEEDFIT_UNDERFLOOR` below is a real, freely-hosted, first-party manufacturer
 * substitute for that: JG Speedfit's own published heat-output tables, tested at
 * BSRIA (an independent UK test house) for three of its four floor constructions,
 * broken down by floor covering and by flow/return temperature. It is NOT the
 * BS EN 1264-2 curve itself, and must not be treated as one — it is one
 * manufacturer's own system, at 15mm PB pipe, kept provisional for exactly that
 * reason alongside the usual "this was read from a PDF, once" gap. See
 * `UNDERFLOOR_CATALOGUES`'s own `toVerify` for what is still owed.
 */

/**
 * The four constructions JG Speedfit's technical guide publishes heat outputs for.
 *
 * "screed" is explicitly NOT independently tested the way the other three are — the
 * source document itself (p.49) says Speedfit commissioned BSRIA testing for the
 * overfit, underfit-board and aluminium-spreader-plate systems specifically, while
 * the screeded-floor figures are described as calculated "using proven mathematical
 * equations and experience". That is a genuine difference in how confident the
 * source itself is, recorded on the entry rather than smoothed away.
 */
export type UnderfloorConstructionType =
  | "overfit-panel"
  | "underfit-board"
  | "aluminium-spreader-plate"
  | "screed";

/** One tested flow/return condition and the output measured at it. */
export interface UnderfloorOutputPoint {
  readonly flowTempC: number;
  readonly returnTempC: number;
  readonly outputWPerM2: number;
}

export interface UnderfloorCatalogueEntry {
  /** Stable key, unique within the catalogue. Quoted in the design pack. */
  readonly id: string;
  readonly manufacturer: string;
  /** The named system, e.g. "JG Speedfit Underfloor". */
  readonly system: string;
  readonly constructionType: UnderfloorConstructionType;
  /** Verbatim from the sheet's own caption under each table, e.g. "Figures based on 15mm PB tube using 150mm pipe centres and a 10mm Plywood laid over." */
  readonly constructionDescription: string;
  readonly pipeCentresMm: number;
  /** The manufacturer's own floor-finish label, e.g. "Tiles". */
  readonly floorFinish: string;
  /** Floor covering thermal resistance, in Tog — the sheet's own unit, not m²K/W. */
  readonly floorCoveringTogValue: number;
  /**
   * The tested points, always the same four flow/return pairs the sheet publishes:
   * 40/30, 45/35, 50/40, 55/45 °C. Deliberately NOT reduced to a single rated output
   * and exponent the way a radiator is: fitting these four points to a power law
   * would be a curve THIS FILE computed, not one JG Speedfit published — the exact
   * thing `RadiatorCatalogueEntry.exponent`'s own doc comment warns against for
   * radiators, and just as true here.
   */
  readonly outputs: readonly UnderfloorOutputPoint[];
}

export interface UnderfloorCatalogue {
  readonly entries: readonly UnderfloorCatalogueEntry[];
  readonly note: CatalogueProvenanceNote;
}

/**
 * The four flow/return pairs every JG Speedfit table is tabulated at, in the sheet's
 * own printed column order. Exported so a consumer (or a test) can iterate them
 * without re-typing the pairs.
 */
export const JG_SPEEDFIT_FLOW_RETURN_CONDITIONS: ReadonlyArray<
  readonly [flowTempC: number, returnTempC: number]
> = [
  [40, 30],
  [45, 35],
  [50, 40],
  [55, 45],
];

/**
 * One printed row: [floorFinish, togValue, output@40/30, output@45/35, output@50/40, output@55/45].
 *
 * Kept as tuples for the same reason `PanelRow` is in emitter-catalogue.ts: the
 * source is a small table and this stays a table, so a column slipped by one is
 * visible here rather than buried in a wall of objects.
 */
type UfhOutputRow = readonly [
  floorFinish: string,
  togValue: number,
  w40_30: number,
  w45_35: number,
  w50_40: number,
  w55_45: number,
];

interface UfhConstructionTable {
  readonly constructionType: UnderfloorConstructionType;
  readonly pipeCentresMm: number;
  readonly constructionDescription: string;
  readonly rows: readonly UfhOutputRow[];
}

/**
 * Transcribed from JG Speedfit's "Installation and Technical Guide", January 2016
 * (ref Z2105-387-0116WEB), "HEAT OUTPUT TABLES (W/M²)", printed TWICE in the same
 * document — page 11 (main body) and page 50 (appendix summary) — for the three dry
 * constructions, and once (page 11 only) for the screeded floor. See
 * `JG_SPEEDFIT_UNDERFLOOR`'s own provenance note for what the two printings agree
 * and disagree on.
 */
const JG_SPEEDFIT_TABLES: readonly UfhConstructionTable[] = [
  {
    constructionType: "overfit-panel",
    pipeCentresMm: 150,
    constructionDescription:
      "15mm PB tube using 150mm pipe centres and a 10mm Plywood laid over.",
    rows: [
      ["Tiles", 0.1, 36, 50, 65, 78],
      ["Thin Timber Finish", 0.5, 32, 45, 58, 70],
      ["Carpet Tiles/Laminate", 1, 29, 40, 52, 64],
      ["Carpet and Underlay", 1.5, 26, 36, 47, 58],
    ],
  },
  {
    constructionType: "underfit-board",
    pipeCentresMm: 200,
    constructionDescription:
      "15mm PB tube using 200mm pipe centres and a 22mm chipboard deck laid over.",
    rows: [
      // The 45/35 figure (20) is LOWER than the 40/30 figure (22) despite the higher
      // flow temperature, and lower than this SAME table's Thin Timber Finish row at
      // 45/35 (28) — physically backwards, since a better (lower-Tog) floor covering
      // should never emit less than a worse one at the same flow condition. This is
      // printed IDENTICALLY on both page 11 and page 50 of the source document, so it
      // is not a one-off scanning glitch; it is the manufacturer's own printed figure.
      // Recorded as printed rather than replaced with a guess — see
      // `JG_SPEEDFIT_UNDERFLOOR`'s provenance note, and the exclusion of this one row
      // from the monotonicity tests in underfloor-catalogue.test.ts.
      ["Tiles", 0.1, 22, 20, 39, 48],
      ["Thin Timber Finish", 0.5, 20, 28, 36, 44],
      ["Carpet Tiles/Laminate", 1, 18, 26, 33, 41],
      ["Carpet and Underlay", 1.5, 17, 22, 31, 38],
    ],
  },
  {
    constructionType: "aluminium-spreader-plate",
    pipeCentresMm: 200,
    constructionDescription:
      "15mm PB tube using 200mm pipe centres and a 22mm chipboard deck laid over.",
    rows: [
      ["Tiles", 0.1, 28, 40, 52, 64],
      ["Thin Timber Finish", 0.5, 26, 36, 47, 58],
      ["Carpet Tiles/Laminate", 1, 24, 33, 43, 53],
      // The 45/35 figure is printed as 31 on page 11 and as 30 on page 50 of the
      // SAME document — every other figure in this row and table matches exactly
      // between the two printings, so this is a genuine one-cell disagreement, not a
      // misread on this file's part. Page 11 (the main body, rather than the
      // appendix summary) is kept here; the discrepancy is recorded rather than
      // silently resolved — see `JG_SPEEDFIT_UNDERFLOOR`'s provenance note.
      ["Carpet and Underlay", 1.5, 22, 31, 39, 48],
    ],
  },
  {
    constructionType: "screed",
    pipeCentresMm: 200,
    constructionDescription:
      "15mm PB tube using 200mm pipe centres and a 65mm screed deck laid over.",
    rows: [
      ["Tiles", 0.1, 60, 80, 95, 120],
      ["Thin Timber Finish", 0.5, 48, 66, 83, 95],
      ["Carpet Tiles/Laminate", 1, 40, 53, 69, 83],
      ["Carpet and Underlay", 1.5, 34, 47, 58, 70],
    ],
  },
];

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function buildJgSpeedfitEntries(): readonly UnderfloorCatalogueEntry[] {
  const entries: UnderfloorCatalogueEntry[] = [];
  for (const table of JG_SPEEDFIT_TABLES) {
    for (const [floorFinish, togValue, w40, w45, w50, w55] of table.rows) {
      entries.push({
        id: `jg-speedfit-${table.constructionType}-${slug(floorFinish)}`,
        manufacturer: "JG Speedfit",
        system: "JG Speedfit Underfloor",
        constructionType: table.constructionType,
        constructionDescription: table.constructionDescription,
        pipeCentresMm: table.pipeCentresMm,
        floorFinish,
        floorCoveringTogValue: togValue,
        outputs: JG_SPEEDFIT_FLOW_RETURN_CONDITIONS.map(
          ([flowTempC, returnTempC], i) => ({
            flowTempC,
            returnTempC,
            outputWPerM2: [w40, w45, w50, w55][i]!,
          }),
        ),
      });
    }
  }
  return entries;
}

export const JG_SPEEDFIT_UNDERFLOOR: Ruleset<UnderfloorCatalogue> = {
  id: "catalogue.underfloor.jg-speedfit",
  version: "0.1.0-provisional",
  effectiveFrom: "2016-01-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard:
      "JG Speedfit Underfloor — Installation and Technical Guide, \"Heat Output Tables (W/m²)\"",
    issue: "January 2016, ref Z2105-387-0116WEB",
    clause:
      "Heat Output Tables (W/m²): 25mm Overfit, 50mm Underfit Board, Aluminium Spreader " +
      "Plates and Screeded Floor, pages 11 and 50",
    issued: "2016-01-01",
    url: "https://www.johnguest.com/sites/jg/files/2022-01/JG-UFH-Tech-Guide-Z2105-387-0116WEB.pdf",
  },
  data: {
    entries: buildJgSpeedfitEntries(),
    note: {
      retrievedFrom:
        "https://www.johnguest.com/sites/jg/files/2022-01/JG-UFH-Tech-Guide-Z2105-387-0116WEB.pdf " +
        "— fetched directly from johnguest.com, the first-party manufacturer (JG Speedfit).",
      retrievedOn: "2026-08-30",
      checks: [
        "All 4 constructions × 4 floor finishes (16 entries, 64 output figures) were transcribed " +
          "from a rendered image of the source PDF's pages 11 and 50, not from machine-extracted " +
          "text — the document's own text layer failed to extract any content at all for the two-page " +
          "cover excerpt tried first, so every figure here was read from the page image directly.",
        "Three of the four constructions — 25mm Overfit, 50mm Underfit Board, Aluminium Spreader " +
          "Plates — are printed TWICE in the same document: once in the main body (page 11) and again " +
          "in an appendix summary (page 50). Every one of their 48 figures was compared cell by cell " +
          "between the two printings: all but one matched exactly. This is genuine corroboration from " +
          "two separately-set pages of the same source, not the same table read twice.",
        "The one exception: Aluminium Spreader Plates, Carpet and Underlay, at 45/35°C flow/return " +
          "reads 31 W/m² on page 11 and 30 W/m² on page 50. Page 11's figure is kept here; the " +
          "disagreement is recorded rather than silently resolved — see the code comment on that entry.",
        "The fourth construction, Screeded Floor, appears only once (page 11) — it is not part of the " +
          "cross-page comparison above, and the source document itself (page 49) says why: Speedfit " +
          "commissioned independent BSRIA testing for the Overfit, Underfit Board and Aluminium " +
          "Spreader Plate systems specifically, while the screeded-floor figures are described as " +
          "calculated \"using proven mathematical equations and experience\" rather than independently " +
          "tested. That is a genuine difference in how confident the source itself is in each figure, " +
          "recorded here rather than smoothed away.",
        "A second, internal anomaly — present identically on BOTH printings, so it is not a scanning " +
          "artefact — is recorded and deliberately NOT corrected: the 50mm Underfit Board, Tiles " +
          "(0.1 Tog) row reads 22, 20, 39, 48 W/m² for 40/30, 45/35, 50/40, 55/45. The 45/35 figure " +
          "(20) is lower than the 40/30 figure (22) despite the higher flow temperature, and lower than " +
          "the same table's Thin Timber Finish row at 45/35 (28) — physically backwards for a better " +
          "floor covering. See the code comment on that entry, and its exclusion from the monotonicity " +
          "tests in underfloor-catalogue.test.ts.",
        "Every OTHER row, in every other construction, increases monotonically across the four " +
          "flow/return columns, and a lower Tog value (a better-conducting floor covering) never gives " +
          "a lower output than a higher one at the same flow/return condition, within the same " +
          "construction — checked mechanically in underfloor-catalogue.test.ts, with the two flagged " +
          "cells above excluded by name rather than by a loose tolerance.",
      ],
      toVerify: [
        "Independent re-transcription by a second person — done once, by one person, in one session, " +
          "exactly like every radiator catalogue in this file.",
        "Confirm the January 2016 edition is still current: JG Speedfit may have revised the Heat " +
          "Output Tables since, and nothing here can detect a superseded sheet.",
        "This document never states the ROOM design temperature its 40/30 … 55/45 flow/return " +
          "figures assume. UK practice and this engine's own EN_1264_UNDERFLOOR_LIMITS default both use " +
          "20°C for an \"occupied\" area, but this specific source does not say so, and treating its " +
          "figures as \"output at ΔT15/20/25/30 K above a 20°C room\" is this file's assumption, not a " +
          "transcribed fact.",
        "Resolve the single-cell discrepancy (31 vs 30 W/m², Aluminium Spreader Plates / Carpet and " +
          "Underlay / 45°C flow / 35°C return) against JG Speedfit directly.",
        "Resolve the anomalous 50mm Underfit Board / Tiles / 45°C flow figure (20 W/m², lower than the " +
          "40/30 figure and lower than the same table's better-insulated row) against JG Speedfit " +
          "directly — every check available in this environment says it looks wrong, but \"looks wrong\" " +
          "is not a source, and no corrected figure has been substituted for it.",
        "This is ONE manufacturer's own system (15mm PB pipe, these specific deck constructions), not " +
          "the BS EN 1264-2 characteristic curve itself, and must not be read as a substitute for a " +
          "project-specific underfloor designer's own output figure — @sts/calc's `underfloorOutputCeiling` " +
          "and `UnderfloorEmitter.manufacturerDesign` still require that separately, for the reasons their " +
          "own doc comments give.",
        "No overall floor build-up depth (screed/deck thickness above the insulation) is recorded beyond " +
          "the deck figure named in each `constructionDescription` string — confirm against the source's " +
          "own construction drawings before using this for a build-up thickness check.",
      ],
    },
  },
};

/**
 * Every underfloor heating catalogue the engine knows about.
 *
 * Deliberately NOT folded into `ALL_RULESETS` or `RULES_BUNDLE_VERSION`, for the same
 * reason `RADIATOR_CATALOGUES` is kept separate — see that constant's own doc comment
 * in emitter-catalogue.ts.
 */
export const UNDERFLOOR_CATALOGUES: readonly Ruleset<UnderfloorCatalogue>[] = [
  JG_SPEEDFIT_UNDERFLOOR,
];
